// src/components/SchoolSettings.tsx
import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, collection, onSnapshot, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db, firebaseConfig } from '../firebaseConfig';
import { useTranslation } from 'react-i18next';
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, updatePassword, signInWithEmailAndPassword } from 'firebase/auth';

interface SchoolSettingsProps {
    user: any;
    onNavigate: (page: string) => void;
}

interface SubAdmin {
    id: string;
    name: string;
    email: string;
    createdAt?: any;
    role: string;
}

export const SchoolSettings: React.FC<SchoolSettingsProps> = ({ user, onNavigate }) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [schoolData, setSchoolData] = useState<any>(null);

    // --- ÉTATS GESTION SOUS-ADMINISTRATEURS ---
    const [subAdmins, setSubAdmins] = useState<SubAdmin[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newAdminName, setNewAdminName] = useState('');
    const [newAdminEmail, setNewAdminEmail] = useState('');
    const [newAdminPassword, setNewAdminPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [creatingAdmin, setCreatingAdmin] = useState(false);

    // --- ÉTATS MODIFICATION MOT DE PASSE SUB-ADMIN ---
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [selectedAdminForPassword, setSelectedAdminForPassword] = useState<SubAdmin | null>(null);
    const [updatedPassword, setUpdatedPassword] = useState('');
    const [showUpdatedPassword, setShowUpdatedPassword] = useState(false);
    const [updatingPassword, setUpdatingPassword] = useState(false);

    const schoolId = localStorage.getItem('gpschoolbus_id');

    // Vérification du rôle Super Admin
    const isSuperAdmin = schoolData && user?.email?.toLowerCase() === schoolData?.adminEmail?.toLowerCase();

    useEffect(() => {
        let unsubscribe: (() => void) | undefined;

        if (schoolId) {
            loadSchoolData();
            unsubscribe = listenToSubAdmins();
        } else {
            setError(t('error_loading'));
            setLoading(false);
        }

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [schoolId]);

    const loadSchoolData = async () => {
        if (!schoolId) return;
        setLoading(true);
        setError('');
        try {
            const docRef = doc(db, 'schools', schoolId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setSchoolData({ id: docSnap.id, ...docSnap.data() });
            } else {
                setError(t('error_not_found'));
            }
        } catch (err) {
            console.error('Erreur chargement:', err);
            setError(t('error_loading'));
        }
        setLoading(false);
    };

    // --- ÉCOUTE DE LA SOUS-COLLECTION ADMINS ---
    const listenToSubAdmins = () => {
        if (!schoolId) return;
        const subAdminsRef = collection(db, 'schools', schoolId, 'admins');
        return onSnapshot(subAdminsRef, (snapshot) => {
            const list = snapshot.docs.map(docSnap => ({
                id: docSnap.id,
                ...docSnap.data()
            } as SubAdmin));
            setSubAdmins(list);
        });
    };

    const handleSave = async () => {
        if (!schoolId || !schoolData) return;
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const docRef = doc(db, 'schools', schoolId);
            await updateDoc(docRef, {
                schoolName: schoolData.schoolName || '',
                schoolAddress: schoolData.schoolAddress || '',
                schoolPhone: schoolData.schoolPhone || '',
                latitude: Number(schoolData.latitude) || 0,
                longitude: Number(schoolData.longitude) || 0,
                updatedAt: new Date().toISOString()
            });
            setSuccess(t('save_success') || 'OK');
            setTimeout(() => setSuccess(''), 4000);
        } catch (err) {
            console.error('Erreur sauvegarde:', err);
            setError(t('error_saving'));
        }
        setSaving(false);
    };

    // --- AJOUT D'UN SOUS-ADMINISTRATEUR ---
    const handleAddSubAdmin = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!isSuperAdmin) {
            alert(t('error_unauthorized', "Seul le Super Admin peut ajouter des administrateurs."));
            return;
        }

        if (!newAdminEmail || !newAdminPassword || !newAdminName) return;

        if (subAdmins.length >= 5) {
            alert(t('err_sub_admin_limit', "Limite atteinte (5 max)"));
            return;
        }

        setCreatingAdmin(true);
        try {
            const secondaryApp = getApps().find(app => app.name === 'SecondaryApp') 
                || initializeApp(firebaseConfig, 'SecondaryApp');
            const secondaryAuth = getAuth(secondaryApp);

            const userCredential = await createUserWithEmailAndPassword(secondaryAuth, newAdminEmail, newAdminPassword);
            const newUid = userCredential.user.uid;

            const adminDocRef = doc(db, 'schools', schoolId!, 'admins', newUid);
            await setDoc(adminDocRef, {
                name: newAdminName,
                email: newAdminEmail.trim().toLowerCase(),
                role: 'sub_admin',
                createdAt: serverTimestamp()
            });

            setNewAdminName('');
            setNewAdminEmail('');
            setNewAdminPassword('');
            setIsModalOpen(false);
            setSuccess(t('sub_admin_created_success', "Administrateur créé avec succès !"));
            setTimeout(() => setSuccess(''), 4000);
        } catch (err: any) {
            console.error("Erreur création sous-admin:", err);
            alert(err.message || err);
        } finally {
            setCreatingAdmin(false);
        }
    };

    // --- MODIFICATION DU MOT DE PASSE D'UN SOUS-ADMIN ---
    const handleUpdateSubAdminPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isSuperAdmin || !selectedAdminForPassword || !updatedPassword) return;

        setUpdatingPassword(true);
        try {
            const secondaryApp = getApps().find(app => app.name === 'SecondaryApp') 
                || initializeApp(firebaseConfig, 'SecondaryApp');
            const secondaryAuth = getAuth(secondaryApp);

            const currentPasswordPrompt = prompt(t('prompt_current_or_temp_auth'));
            if (!currentPasswordPrompt) {
                setUpdatingPassword(false);
                return;
            }

            const userCred = await signInWithEmailAndPassword(secondaryAuth, selectedAdminForPassword.email, currentPasswordPrompt);
            await updatePassword(userCred.user, updatedPassword);

            setUpdatedPassword('');
            setIsPasswordModalOpen(false);
            setSelectedAdminForPassword(null);
            setSuccess(t('password_updated_success'));
            setTimeout(() => setSuccess(''), 4000);
        } catch (err: any) {
            console.error("Erreur modification mot de passe:", err);
            alert(err.message || t('error_updating_password'));
        } finally {
            setUpdatingPassword(false);
        }
    };

    // --- SUPPRESSION D'UN SOUS-ADMINISTRATEUR ---
    const handleDeleteSubAdmin = async (adminId: string, adminName: string) => {
        if (!isSuperAdmin) {
            alert(t('error_unauthorized', "Seul le Super Admin peut supprimer des administrateurs."));
            return;
        }

        if (!window.confirm(`${t('confirm_delete', 'Voulez-vous supprimer')} ${adminName} ?`)) return;
        try {
            await deleteDoc(doc(db, 'schools', schoolId!, 'admins', adminId));
            setSuccess(t('sub_admin_deleted_success', "Administrateur supprimé avec succès !"));
            setTimeout(() => setSuccess(''), 4000);
        } catch (err) {
            console.error("Erreur suppression:", err);
            setError(t('error_deleting'));
        }
    };

    if (loading) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px' }}>⏳</div>
                <p>{t('loading_data')}</p>
            </div>
        );
    }

    if (error && !schoolData) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <h2>❌ {t('error_loading')}</h2>
                <p style={{ color: 'red' }}>{error}</p>
                <button 
                    onClick={loadSchoolData}
                    style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: '#ffc107', cursor: 'pointer', fontWeight: 'bold' }}
                >
                    {t('retry')}
                </button>
            </div>
        );
    }

    return (
        <div style={{ padding: '30px', maxWidth: '700px', margin: '0 auto', direction: isRTL ? 'rtl' : 'ltr' }}>
            <h1 style={{ marginBottom: '30px' }}>⚙️ {t('menu_settings')}</h1>

            {error && (
                <div style={{ color: 'red', padding: '12px', backgroundColor: '#fff5f5', borderRadius: '8px', marginBottom: '15px', border: '1px solid #fecaca' }}>
                    ❌ {error}
                </div>
            )}
            {success && (
                <div style={{ color: 'green', padding: '12px', backgroundColor: '#f0fff4', borderRadius: '8px', marginBottom: '15px', border: '1px solid #86efac' }}>
                    ✅ {success}
                </div>
            )}

            {schoolData && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                    {/* Nom de l'école */}
                    <div>
                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>
                            🏫 {t('school_name')}
                        </label>
                        <input 
                            type="text" 
                            value={schoolData.schoolName || ''} 
                            onChange={(e) => setSchoolData({...schoolData, schoolName: e.target.value})}
                            style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '16px', boxSizing: 'border-box' }}
                        />
                    </div>

                    {/* Adresse */}
                    <div>
                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>
                            📍 {t('address')}
                        </label>
                        <input 
                            type="text" 
                            value={schoolData.schoolAddress || ''} 
                            onChange={(e) => setSchoolData({...schoolData, schoolAddress: e.target.value})}
                            style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '16px', boxSizing: 'border-box' }}
                        />
                    </div>

                    {/* Téléphone */}
                    <div>
                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>
                            📞 {t('phone')}
                        </label>
                        <input 
                            type="text" 
                            value={schoolData.schoolPhone || ''} 
                            onChange={(e) => setSchoolData({...schoolData, schoolPhone: e.target.value})}
                            style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '16px', boxSizing: 'border-box' }}
                        />
                    </div>

                    {/* GPS */}
                    <div style={{ borderTop: '2px solid #e2e8f0', paddingTop: '20px', marginTop: '10px' }}>
                        <h3>📍 {t('gps_coordinates')}</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                            <div>
                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>
                                    {t('latitude')}
                                </label>
                                <input 
                                    type="text"
                                    value={schoolData.latitude ?? ''} 
                                    onChange={(e) => setSchoolData({...schoolData, latitude: e.target.value})}
                                    style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '16px', boxSizing: 'border-box' }}
                                />
                            </div>
                            <div>
                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>
                                    {t('longitude')}
                                </label>
                                <input 
                                    type="text"
                                    value={schoolData.longitude ?? ''} 
                                    onChange={(e) => setSchoolData({...schoolData, longitude: e.target.value})}
                                    style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '16px', boxSizing: 'border-box' }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* --- SECTION : GESTION MULTI-ADMINS --- */}
                    <div style={{ borderTop: '2px solid #e2e8f0', paddingTop: '20px', marginTop: '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                            <div>
                                <h3 style={{ margin: 0 }}>👥 {t('admin_team_title', 'Équipe Administrateurs')} ({subAdmins.length}/5)</h3>
                                <p style={{ margin: '3px 0 0 0', fontSize: '12px', color: '#718096' }}>
                                    {t('admin_team_subtitle', 'Autorisez des collaborateurs à se connecter simultanément.')}
                                </p>
                            </div>
                            
                            {isSuperAdmin && subAdmins.length < 5 && (
                                <button 
                                    type="button"
                                    onClick={() => setIsModalOpen(true)}
                                    style={{
                                        padding: '8px 14px',
                                        backgroundColor: '#2d3748',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontWeight: 'bold',
                                        fontSize: '13px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    + {t('btn_add_admin', t('btn_add', 'Ajouter'))}
                                </button>
                            )}
                        </div>

                        {/* Liste des comptes */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '15px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                                <div>
                                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{t('super_admin_main_account')}</div>
                                    <div style={{ fontSize: '12px', color: '#718096' }}>📧 {schoolData.adminEmail}</div>
                                </div>
                                <span style={{ fontSize: '11px', background: '#fef3c7', color: '#92400e', padding: '3px 8px', borderRadius: '12px', fontWeight: 'bold' }}>
                                    {t('badge_super_admin', 'Super Admin')}
                                </span>
                            </div>

                            {subAdmins.map((sub) => (
                                <div key={sub.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #cbd5e1', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                                    <div>
                                        <div style={{ fontWeight: 'bold', fontSize: '14px' }}>👤 {sub.name}</div>
                                        <div style={{ fontSize: '12px', color: '#64748b' }}>📧 {sub.email}</div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span style={{ fontSize: '11px', background: '#e0f2fe', color: '#0369a1', padding: '3px 8px', borderRadius: '12px', fontWeight: 'bold' }}>
                                            {t('badge_sub_admin', 'Sub Admin')}
                                        </span>
                                        {isSuperAdmin && (
                                            <>
                                                <button 
                                                    type="button"
                                                    onClick={() => {
                                                        setSelectedAdminForPassword(sub);
                                                        setUpdatedPassword('');
                                                        setIsPasswordModalOpen(true);
                                                    }}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}
                                                    title={t('modal_change_password_title')} // Supprimez le second argument en dur si la clé existe dans vos fichiers i18n
                                                >
                                                    🔑
                                                </button>
                                                <button 
                                                    type="button"
                                                    onClick={() => handleDeleteSubAdmin(sub.id, sub.name)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}
                                                    title={t('btn_delete')} // Idem ici
                                                >
                                                    🗑️
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ID École (lecture seule) */}
                    <div style={{ marginTop: '10px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                        <span style={{ fontSize: '13px', color: '#718096' }}>
                            🆔 {t('school_id')} : <strong>{schoolData.schoolId || 'N/A'}</strong>
                        </span>
                        <span style={{ fontSize: '13px', color: '#718096', marginLeft: isRTL ? '0' : '20px', marginRight: isRTL ? '20px' : '0' }}>
                            📧 {schoolData.adminEmail || 'N/A'}
                        </span>
                    </div>

                    {/* Bouton Enregistrer */}
                    <button 
                        onClick={handleSave} 
                        disabled={saving}
                        style={{ 
                            padding: '15px', 
                            backgroundColor: '#ffc107', 
                            color: 'black', 
                            border: 'none', 
                            borderRadius: '12px', 
                            fontWeight: 'bold', 
                            fontSize: '16px',
                            cursor: 'pointer',
                            marginTop: '10px',
                            opacity: saving ? 0.7 : 1
                        }}
                    >
                        {saving ? t('saving') : t('save')}
                    </button>
                </div>
            )}

            {/* --- MODAL AJOUT D'ADMINISTRATEUR --- */}
            {isModalOpen && isSuperAdmin && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '25px',
                        borderRadius: '15px',
                        width: '90%',
                        maxWidth: '420px',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                        direction: isRTL ? 'rtl' : 'ltr'
                    }}>
                        <h2 style={{ marginTop: 0, fontSize: '18px' }}>👤 {t('add_sub_admin_title', 'Ajouter un administrateur')}</h2>
                        <form onSubmit={handleAddSubAdmin} style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '15px' }}>
                            <div>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#475569' }}>{t('p_full_name', 'Nom complet')}</label>
                                <input 
                                    type="text" 
                                    placeholder={t('p_full_name', 'Nom complet')} 
                                    value={newAdminName} 
                                    onChange={e => setNewAdminName(e.target.value)}
                                    required 
                                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', marginTop: '4px', boxSizing: 'border-box' }}
                                />
                            </div>
                            <div>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#475569' }}>{t('placeholder_email', 'Email')}</label>
                                <input 
                                    type="email" 
                                    placeholder="transport@school.com" 
                                    value={newAdminEmail} 
                                    onChange={e => setNewAdminEmail(e.target.value)}
                                    required 
                                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', marginTop: '4px', boxSizing: 'border-box' }}
                                />
                            </div>
                            <div>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#475569' }}>{t('placeholder_password', 'Mot de passe')}</label>
                                <div style={{ position: 'relative', marginTop: '4px' }}>
                                    <input 
                                        type={showPassword ? "text" : "password"} 
                                        placeholder={t('placeholder_password', 'Mot de passe')} 
                                        value={newAdminPassword} 
                                        onChange={e => setNewAdminPassword(e.target.value)}
                                        required 
                                        style={{ width: '100%', padding: '10px', paddingRight: isRTL ? '10px' : '40px', paddingLeft: isRTL ? '40px' : '10px', borderRadius: '8px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        style={{
                                            position: 'absolute',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            right: isRTL ? 'unset' : '10px',
                                            left: isRTL ? '10px' : 'unset',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: '16px'
                                        }}
                                        title={showPassword ? t('hide', 'Masquer') : t('show', 'Afficher')}
                                    >
                                        {showPassword ? "👁️‍🗨️" : "👁️"}
                                    </button>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '6px', fontSize: '11px', color: '#64748b', lineHeight: '1.4' }}>
                                    <span style={{ fontSize: '13px' }}>ℹ️</span>
                                    <span>{t('email_security_warning', "Sécurité : Si un e-mail est créé puis supprimé, il ne pourra plus être utilisé directement. Pour le réutiliser, une demande doit être envoyée à admin.gpschoolbus.com.")}</span>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '10px', marginTop: '15px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                                <button 
                                    type="button" 
                                    onClick={() => setIsModalOpen(false)}
                                    style={{ flex: 1, padding: '10px', background: '#e2e8f0', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                    {t('btn_cancel', 'Annuler')}
                                </button>
                                <button 
                                    type="submit" 
                                    disabled={creatingAdmin}
                                    style={{ flex: 1, padding: '10px', background: '#ffc107', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                    {creatingAdmin ? "..." : `+ ${t('btn_add', 'Ajouter')}`}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* --- MODAL MODIFICATION MOT DE PASSE SUB-ADMIN --- */}
            {isPasswordModalOpen && selectedAdminForPassword && isSuperAdmin && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '25px',
                        borderRadius: '15px',
                        width: '90%',
                        maxWidth: '420px',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                        direction: isRTL ? 'rtl' : 'ltr'
                    }}>
                        <h2 style={{ marginTop: 0, fontSize: '18px' }}>🔑 {t('modal_change_password_title')}</h2>
                        <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 15px 0' }}>
                            👤 <strong>{selectedAdminForPassword.name}</strong> ({selectedAdminForPassword.email})
                        </p>
                        <form onSubmit={handleUpdateSubAdminPassword} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#475569' }}>{t('new_password_label', 'Nouveau mot de passe')}</label>
                                <div style={{ position: 'relative', marginTop: '4px' }}>
                                    <input 
                                        type={showUpdatedPassword ? "text" : "password"} 
                                        placeholder="••••••••" 
                                        value={updatedPassword} 
                                        onChange={e => setUpdatedPassword(e.target.value)}
                                        required 
                                        style={{ width: '100%', padding: '10px', paddingRight: isRTL ? '10px' : '40px', paddingLeft: isRTL ? '40px' : '10px', borderRadius: '8px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowUpdatedPassword(!showUpdatedPassword)}
                                        style={{
                                            position: 'absolute',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            right: isRTL ? 'unset' : '10px',
                                            left: isRTL ? '10px' : 'unset',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: '16px'
                                        }}
                                        title={showUpdatedPassword ? t('hide', 'Masquer') : t('show', 'Afficher')}
                                    >
                                        {showUpdatedPassword ? "👁️‍🗨️" : "👁️"}
                                    </button>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '10px', marginTop: '15px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                                <button 
                                    type="button" 
                                    onClick={() => {
                                        setIsPasswordModalOpen(false);
                                        setSelectedAdminForPassword(null);
                                    }}
                                    style={{ flex: 1, padding: '10px', background: '#e2e8f0', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                    {t('btn_cancel', 'Annuler')}
                                </button>
                                <button 
                                    type="submit" 
                                    disabled={updatingPassword}
                                    style={{ flex: 1, padding: '10px', background: '#ffc107', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                    {updatingPassword ? "..." : t('btn_update', 'Mettre à jour')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};